import { SecurityContext } from 'gossamer-unified';
import { PERSONAL_INSTITUTION_ID } from './constants';

const STAFF_MEMBER_USER_IDS = [
  // local
  '968b9806-7d25-47ee-8704-95588f9818df', // stephen.d.francis@gmail.com
  '852d8f6c-36d2-40e7-a092-5be16b1f1021', // stephen.f@careerbook.co.uk (XPS13U)
  // staging
  'ebd06d55-e23c-4792-ad49-18d4664c7ab6', // james.w@careerbook.co.uk
  '576d172d-ea91-463a-8530-29b087dd0355', // joanna.w@careerbook.co.uk
  '917ab66f-0a3f-4232-a79c-fb8c6d9b0536', // laura.f@careerbook.co.uk
  '0017fd1c-2439-407e-9827-6854e24a2c23', // stephen.d.francis@gmail.com
  'ac88ca26-2aca-4962-84cd-cd4b9014467d', // stephen.f@careerbook.co.uk
  // prod
  'ebd06d55-e23c-4792-ad49-18d4664c7ab6', // james.w@careerbook.co.uk
  'af38321d-966c-4313-9f9a-ccd4d4e8042b', // joanna.w@careerbook.co.uk
  '11ba1bb3-89cb-43be-8815-7c8153adfd30', // laura.f@careerbook.co.uk
  '5acd6d96-f259-4afd-b4ad-85d72589da54', // stephen.d.francis@gmail.com
  '4fbf8a3a-5647-4511-aa44-6e3d2bc27fae', // stephen.f@careerbook.co.uk
];

export interface CustomInfo {
  photo: string | undefined;
  accessInstitutionIds: number[];
  accessOrgUnitIds: number[];
  adminInstitutionId: number | null;
  adminInstitutionType: string | null;
  adminOrgUnitId: number | null;
  followingInstitutionIds: number[];
  createEventInstitutionId: number | null;
  createEventInstitutionType: string | null;
}

export const canLeaveInstitution = (
  customInfo: CustomInfo | undefined,
  institutionId: number,
  institutionType: string | undefined,
): boolean => {
  if (!customInfo || !institutionType || !customInfo.followingInstitutionIds.includes(institutionId)) {
    return false;
  }
  if (customInfo.adminInstitutionId === institutionId) {
    return false;
  }
  return !(
    institutionType.trim() === 'enterprise' &&
    customInfo.accessInstitutionIds.includes(institutionId)
  );
};

export interface InstitutionMembership {
  institution_id: number;
  institution_type: string;
  is_admin: boolean;
  is_verified: boolean;
  created_at: Date;
  who_can_create_events: string;
}

export interface OrgUnitMembership {
  org_unit_id: number;
  is_admin: boolean;
  is_verified: boolean;
  created_at: Date;
  who_can_create_events: string;
}

export const getSecurityContext = (
  userId: string,
  firstName: string,
  lastName: string,
  email: string,
  userPhoto: string | undefined,
  insts: InstitutionMembership[],
  orgUnits: OrgUnitMembership[],
) => {
  const isStaffMember = STAFF_MEMBER_USER_IDS.includes(userId);

  try {
    insts.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return bTime - aTime;
    });
  } catch (error) {
    console.error(`[getSecurityContext] Error sorting institutions by created_at for userId ${userId}:`, {
      error: error instanceof Error ? error.message : String(error),
      instSample: insts[0] ? { created_at: insts[0].created_at, type: typeof insts[0].created_at } : 'no insts',
    });
    throw error;
  }

  const newestVerifiedInstitution = insts.find((inst) => inst.is_verified);
  const newestFollowingInstitution = insts[0];

  // admins of personal institutions aren't really admins...
  const adminInstitution = insts.find(
    (inst) => inst.is_admin && inst.is_verified && inst.institution_type.trim() !== 'personal',
  );
  const adminInstitutionId = adminInstitution?.institution_id || null;
  const adminInstitutionType = adminInstitution?.institution_type.trim() || null;

  // institution id to be used when user creates a new event - prefer the institution the user is admin of,
  //  else the latest one they are a verified member of which permits verified members creating events...
  const allowedToCreateEventsForInstitution =
    insts.find((inst) => inst.is_admin && inst.is_verified) ||
    insts.find((inst) => inst.who_can_create_events.trim() === 'admins_or_verified' && inst.is_verified);

  const accessInstitutionIds = insts.filter((inst) => inst.is_verified).map((inst) => Number(inst.institution_id));
  const followingInstitutionIds = insts.map((inst) => Number(inst.institution_id));

  if (accessInstitutionIds.length === 0) {
    accessInstitutionIds.push(-1); // ensure SQL conditions are always syntactically valid, i.e. ...IN ({accessInstitutionIds}) contains at least one element
  }

  const primaryInstitutionId =
    adminInstitutionId ?? newestVerifiedInstitution?.institution_id ?? newestFollowingInstitution?.institution_id ?? '';

  const roleId = isStaffMember ? 'staff' : typeof adminInstitutionId === 'number' ? 'admin' : 'user';
  const customInfo: CustomInfo = {
    photo: userPhoto,
    accessInstitutionIds,
    accessOrgUnitIds: orgUnits.filter((orgUnit) => orgUnit.is_verified).map((orgUnit) => Number(orgUnit.org_unit_id)),
    adminOrgUnitId: orgUnits.find((orgUnit) => orgUnit.is_admin && orgUnit.is_verified)?.org_unit_id || -1,
    adminInstitutionId,
    adminInstitutionType,
    followingInstitutionIds,
    createEventInstitutionId: allowedToCreateEventsForInstitution?.institution_id || PERSONAL_INSTITUTION_ID,
    createEventInstitutionType: allowedToCreateEventsForInstitution?.institution_type?.trim() || 'personal',
  };
  const securityContext: SecurityContext = {
    name: [firstName, lastName].join(' '),
    email,
    userId,
    roleId,
    orgPath: String(primaryInstitutionId),
    customInfo,
  };
  return securityContext;
};
